/**
 * FmShellLayout — top-panel shell layout. The ONLY shell for /app/* (mywox.3).
 *
 * E1 shell composition (all sibling tasks merged):
 *   FmTopBar (T2) + FmMapTools/FmContextPanel floating overlays (T3, 40a2l.16) +
 *   AssistantDock (T4) + FmScrubberSlot/FmVerdictChipSlot (T6)
 *
 * Provider stack (outermost→innermost):
 *   AssistantDockProvider → SelectedStandProvider → FmContextPanelProvider → FmShellSlotsProvider
 *
 * data-testid="fm-shell-layout" — smoke tests assert this.
 */
import React from 'react';
import { Outlet } from 'react-router-dom';
import FmTopBar from '@/shared/ui/FmTopBar';
import FmMapTools from '@/shared/ui/FmMapTools';
import FmContextPanel from '@/shared/ui/FmContextPanel';
import AssistantDock from '@/shared/ui/AssistantDock';
import { FmScrubberSlot, FmVerdictChipSlot } from '@/shared/ui';
import { AssistantDockProvider } from '@/contexts/AssistantDockContext';
import { SelectedStandProvider } from '@/contexts/SelectedStandContext';
import { FmContextPanelProvider } from '@/contexts/FmContextPanelContext';
import { FmShellSlotsProvider, useFmShellSlots } from '@/contexts/FmShellSlotsContext';
import { useFmContextPanel } from '@/contexts/fmContextPanelInternals';
import { useSyncSelectedPropertyToRoute } from '@/hooks/useSyncSelectedPropertyToRoute';
import { useShellBleedWidth } from '@/components/ui/useShellBleedWidth';
import './AppLayout.css';

type FmShellSlotVars = React.CSSProperties & {
  '--fm-scrubber-slot-h'?: string;
  '--fm-scrubber-slot-h-mobile'?: string;
};

/**
 * Inner shell that reads live contexts for data-* attributes.
 * Must be inside FmContextPanelProvider to call useFmContextPanel().
 */
const FmShellInner: React.FC = () => {
  // Record the forest the user is viewing (per-property route `:propertyId`) into
  // the global property store so /app/watch and other per-property consumers
  // resolve the open forest without a manual localStorage seed (s1ohk.14).
  useSyncSelectedPropertyToRoute();
  const { isPanelOpen, mapToolsPlacement, panels } = useFmContextPanel();
  const { scrubber, verdict, scrubberHeight, scrubberMobileHeight } = useFmShellSlots();
  // The floating map-tools pill is pinned top-left over the content host. On a
  // reading route the column reaches the left edge at tablet/mobile widths, so
  // the pinned pill would occlude the page H1 and card titles. Expose whether
  // the pill is actually showing so the shell can reserve its keep-out gutter;
  // full-bleed spatial archetypes continue to own their local control geometry.
  const hasShellMapTools = mapToolsPlacement === 'shell' && panels.length > 0;
  const rootRef = React.useRef<HTMLDivElement>(null);
  const outletRef = React.useRef<HTMLDivElement>(null);
  useShellBleedWidth(rootRef, outletRef);
  const slotVars = React.useMemo<FmShellSlotVars>(() => {
    const nextVars: FmShellSlotVars = {};
    if (scrubberHeight) nextVars['--fm-scrubber-slot-h'] = scrubberHeight;
    if (scrubberMobileHeight) nextVars['--fm-scrubber-slot-h-mobile'] = scrubberMobileHeight;
    return nextVars;
  }, [scrubberHeight, scrubberMobileHeight]);

  return (
    <div
      ref={rootRef}
      className="app-shell-root"
      data-testid="fm-shell-layout"
      data-panel-open={String(isPanelOpen)}
      data-has-maptools={String(hasShellMapTools)}
      style={slotVars}
    >
      {/* Main content area: top bar + routed content with floating overlays.
          Plain Fm divs — NO AntD <Layout>/<Content> (they injected AntD's flat
          default theme behind the translucent glass chrome). AntD is contained to
          controls/forms only; the shell is Fm-owned (3jq2g.29). */}
      <div className="app-main-layout" data-fm-topbar-scope>
        <FmTopBar />
        {/* data-testid="app-sidebar": established shell-presence anchor across the full test suite. */}
        {/* The docked sidebar is gone (40a2l E1); this testid is retained on the content div as the */}
        {/* suite-wide shell-presence anchor. Do NOT rename — ~24 specs depend on it. */}
        <div data-testid="app-sidebar" className="app-shell-content">
          {/* Floating glass overlays (position:absolute via CSS) */}
          {mapToolsPlacement === 'shell' ? (
            <>
              <FmMapTools />
              <FmContextPanel />
            </>
          ) : null}
          <div ref={outletRef} data-testid="shell-outlet" className="app-shell-outlet">
            <Outlet />
          </div>
        </div>
      </div>

      {/* Assistant dock (T4): omnipresent on every protected route */}
      <AssistantDock />

      {/* Shell slots (T6): zero-height by default; consuming pages populate */}
      <FmScrubberSlot>{scrubber}</FmScrubberSlot>
      <FmVerdictChipSlot>{verdict}</FmVerdictChipSlot>
    </div>
  );
};

/**
 * FmShellLayout — provider wrapper + inner shell.
 * data-testid is on FmShellInner to satisfy smoke tests.
 */
const FmShellLayout: React.FC = () => (
  <AssistantDockProvider>
    <SelectedStandProvider>
      <FmContextPanelProvider>
        <FmShellSlotsProvider>
          <FmShellInner />
        </FmShellSlotsProvider>
      </FmContextPanelProvider>
    </SelectedStandProvider>
  </AssistantDockProvider>
);

export default FmShellLayout;
